import React, { useState, useRef, useEffect, Component } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import Icon from '../Icon/Icon';
import './AlternativePdfViewer.scss';

// Catches PDF.js worker errors (e.g. sendWithPromise after unmount) so the app doesn't crash
class PdfErrorBoundary extends Component {
    state = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    render() {
        if (this.state.hasError) {
            return <div className="pdf-error">Error loading PDF. Please try again.</div>;
        }
        return this.props.children;
    }
}

// Set the worker source with fallback options
const setWorkerSource = () => {
  try {
    // Use local worker file first (avoids CSP issues and is faster)
    pdfjs.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.mjs';
  } catch (error) {
    console.warn('Failed to set local worker, falling back to CDN:', error);
    // Fallback to CDN with explicit HTTPS (required for CSP)
    pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
  }
};

setWorkerSource();

const AlternativePdfViewer = ({ pdfUrl, isPreview = false }) => {
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [scale, setScale] = useState(1.0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [readyToRenderPdf, setReadyToRenderPdf] = useState(false);
    const containerRef = useRef(null);
    const controlsTimeoutRef = useRef(null);
    const isMountedRef = useRef(true);
    const deferTimerRef = useRef(null);

    // Pinch zoom refs
    const touchStartRef = useRef(null);
    const initialDistanceRef = useRef(null);
    const initialScaleRef = useRef(null);

    // Check if we're on iOS Safari
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    // Defer rendering Document so React Strict Mode / quick unmount don't leave PDF.js worker in a bad state
    useEffect(() => {
        isMountedRef.current = true;
        deferTimerRef.current = setTimeout(() => {
            if (isMountedRef.current) {
                setReadyToRenderPdf(true);
            }
        }, 100);
        return () => {
            isMountedRef.current = false;
            if (deferTimerRef.current) {
                clearTimeout(deferTimerRef.current);
                deferTimerRef.current = null;
            }
        };
    }, []);

    function onDocumentLoadSuccess({ numPages: totalPages }) {
        if (isMountedRef.current) {
            setNumPages(totalPages);
        }
    }

    // Calculate distance between two touch points
    const getDistance = (touch1, touch2) => {
        const dx = touch1.clientX - touch2.clientX;
        const dy = touch1.clientY - touch2.clientY;
        return Math.sqrt(dx * dx + dy * dy);
    };

    // Handle pinch zoom gestures
    const handleTouchStart = (e) => {
        e.stopPropagation();
        
        if (e.touches.length === 2) {
            // Two finger touch - start pinch gesture
            touchStartRef.current = {
                touch1: { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY },
                touch2: { clientX: e.touches[1].clientX, clientY: e.touches[1].clientY }
            };
            initialDistanceRef.current = getDistance(touchStartRef.current.touch1, touchStartRef.current.touch2);
            initialScaleRef.current = scale;
        } else if (e.touches.length === 1) {
            // Single touch - show controls
            setShowControls(true);
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
            }
            controlsTimeoutRef.current = setTimeout(() => {
                setShowControls(false);
            }, 3000);
        }
    };

    const handleTouchMove = (e) => {
        e.stopPropagation();
        
        if (e.touches.length === 2 && touchStartRef.current && initialDistanceRef.current) {
            // Two finger touch - handle pinch zoom
            e.preventDefault(); // Prevent default to avoid scrolling during pinch
            
            const currentTouch1 = { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
            const currentTouch2 = { clientX: e.touches[1].clientX, clientY: e.touches[1].clientY };
            const currentDistance = getDistance(currentTouch1, currentTouch2);
            
            if (initialDistanceRef.current > 0) {
                const scaleFactor = currentDistance / initialDistanceRef.current;
                const newScale = Math.max(0.5, Math.min(3.0, initialScaleRef.current * scaleFactor));
                setScale(newScale);
            }
        }
    };

    const handleTouchEnd = (e) => {
        e.stopPropagation();
        
        if (e.touches.length < 2) {
            // Reset pinch gesture state
            touchStartRef.current = null;
            initialDistanceRef.current = null;
            initialScaleRef.current = null;
        }
    };

    // Auto-hide controls in fullscreen mode after 3 seconds of inactivity
    useEffect(() => {
        if (isFullscreen) {
            const handleMouseMove = () => {
                setShowControls(true);
                if (controlsTimeoutRef.current) {
                    clearTimeout(controlsTimeoutRef.current);
                }
                controlsTimeoutRef.current = setTimeout(() => {
                    setShowControls(false);
                }, 3000);
            };

            const handleTouchStart = () => {
                setShowControls(true);
                if (controlsTimeoutRef.current) {
                    clearTimeout(controlsTimeoutRef.current);
                }
                controlsTimeoutRef.current = setTimeout(() => {
                    setShowControls(false);
                }, 3000);
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('touchstart', handleTouchStart);

            // Show controls initially
            setShowControls(true);
            controlsTimeoutRef.current = setTimeout(() => {
                setShowControls(false);
            }, 3000);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('touchstart', handleTouchStart);
                if (controlsTimeoutRef.current) {
                    clearTimeout(controlsTimeoutRef.current);
                }
            };
        }
    }, [isFullscreen]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
            }
        };
    }, []);

    const goToPrevPage = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (pageNumber > 1) {
            setPageNumber(pageNumber - 1);
        }
    };

    const goToNextPage = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (pageNumber < numPages) {
            setPageNumber(pageNumber + 1);
        }
    };

    const zoomIn = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setScale(Math.min(3.0, scale + 0.1));
    };

    const zoomOut = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setScale(Math.max(0.5, scale - 0.1));
    };

    const toggleFullscreen = () => {
        const element = containerRef.current;
        if (!element) return;

        if (isIOS) {
            // For iOS, we'll use a custom fullscreen implementation
            if (!isFullscreen) {
                setIsFullscreen(true);
                document.documentElement.classList.add('ios-fullscreen');
                element.classList.add('ios-fullscreen');
                // Lock screen orientation if possible
                if (screen.orientation && screen.orientation.lock) {
                    screen.orientation.lock('landscape').catch(() => {});
                }
                // Prevent body scroll
                document.body.style.overflow = 'hidden';
            } else {
                setIsFullscreen(false);
                document.documentElement.classList.remove('ios-fullscreen');
                element.classList.remove('ios-fullscreen');
                // Unlock screen orientation if possible
                if (screen.orientation && screen.orientation.unlock) {
                    screen.orientation.unlock();
                }
                // Restore body scroll
                document.body.style.overflow = '';
            }
            return;
        }

        // Regular fullscreen for other browsers
        if (!document.fullscreenElement && 
            !document.webkitFullscreenElement && 
            !document.mozFullScreenElement && 
            !document.msFullscreenElement) {
            if (element.requestFullscreen) {
                element.requestFullscreen();
            } else if (element.webkitRequestFullscreen) {
                element.webkitRequestFullscreen();
            } else if (element.mozRequestFullScreen) {
                element.mozRequestFullScreen();
            } else if (element.msRequestFullscreen) {
                element.msRequestFullscreen();
            }
            setIsFullscreen(true);
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
            setIsFullscreen(false);
        }
    };

    // Handle fullscreen change events for non-iOS browsers
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!(document.fullscreenElement || 
                              document.webkitFullscreenElement || 
                              document.mozFullScreenElement || 
                              document.msFullscreenElement));
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    return (
        <div className={`alternative-pdf-viewer ${isPreview ? 'preview' : ''} ${isFullscreen ? 'fullscreen-active' : ''}`} ref={containerRef}>
            {!isPreview && (
                <button 
                    className={`fullscreen-button ${isFullscreen ? 'fullscreen-active' : ''}`}
                    onClick={toggleFullscreen}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >
                    <div className="controls__icon">
                        <Icon 
                            alias={isFullscreen ? "minimize" : "maximize"} 
                            stroke="#fff" 
                            width={20} 
                        />
                    </div>
                </button>
            )}
            <div 
                className="pdf-container"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {!readyToRenderPdf ? (
                    <div className="pdf-loading">Loading PDF...</div>
                ) : (
                    <PdfErrorBoundary>
                        <Document
                            key={pdfUrl}
                            file={pdfUrl}
                            onLoadSuccess={onDocumentLoadSuccess}
                            loading={<div className="pdf-loading">Loading PDF...</div>}
                            error={<div className="pdf-error">Error loading PDF. Please try again.</div>}
                        >
                            <Page
                                pageNumber={isPreview ? 1 : pageNumber}
                                scale={scale}
                                renderTextLayer={!isPreview}
                                renderAnnotationLayer={!isPreview}
                            />
                        </Document>
                    </PdfErrorBoundary>
                )}
            </div>
            {!isPreview && (
                <div className={`controls ${isFullscreen ? 'fullscreen-controls' : ''} ${showControls ? 'show' : 'hide'}`}>
                    <div className="controls__group">
                        <button 
                            className="controls__button" 
                            onClick={goToPrevPage}
                            onTouchStart={handleTouchStart}
                            onTouchEnd={handleTouchEnd}
                            disabled={pageNumber <= 1}
                        >
                            <div className="controls__icon">
                                <Icon alias="chevron-left" stroke='#fff' width={32} />
                            </div>
                        </button>
                        <span className="controls__page-number">
                            {pageNumber} / {numPages || '--'}
                        </span>
                        <button 
                            className="controls__button" 
                            onClick={goToNextPage}
                            onTouchStart={handleTouchStart}
                            onTouchEnd={handleTouchEnd}
                            disabled={pageNumber >= numPages}
                        >
                            <div className="controls__icon">
                                <Icon alias="arrow-right" stroke='#fff' width={32}/>
                            </div>
                        </button>
                    </div>
                    <div className="controls__group">
                        <button 
                            className="controls__button" 
                            onClick={zoomOut}
                            onTouchStart={handleTouchStart}
                            onTouchEnd={handleTouchEnd}
                        >
                            <div className="controls__icon">-</div>
                        </button>
                        <span className="controls__zoom">{Math.round(scale * 100)}%</span>
                        <button 
                            className="controls__button" 
                            onClick={zoomIn}
                            onTouchStart={handleTouchStart}
                            onTouchEnd={handleTouchEnd}
                        >
                            <div className="controls__icon">+</div>
                        </button>
                    </div>
                </div>
            )}
            {isPreview && (
                <div className="preview-overlay">
                    <div>
                      <span className="locked-text">LOCKED</span>
                      <span className="unlock-text">Unlock by purchasing the full document below</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AlternativePdfViewer; 